import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgxSmartModalComponent } from 'ngx-smart-modal';
import { Subscription } from 'rxjs';

import { ButtonIconProps, ButtonProps } from '@yooga-tecnologia/mantra/loader';

import { Phone } from '../../../components/flag-phone-input/flag-phone-input.component';
import { AppBarComponent } from '../../../components/app-bar/app-bar.component';
import { EventTrackingEnum, PageStatusEnum, SCREEN_VIEW_TIMEOUT_IN_MS } from '../../../app.types';
import { LoadingComponent } from '../../../components/loading/loading.component';

import { CartService } from '../../../domain/services/cart.service';
import { UserService } from '../../../domain/services/user.service';
import { CurrencyPipe } from '../../../pipes/currency.pipe';
import { TableService } from '../../../domain/services/table.service';
import { User } from '../../../domain/models/user';
import { StatusMessageComponent } from '../../../components/status-message/status-message.component';
import { EventService } from '../../../domain/services/event.service';
import { Table } from '../../../domain/models/table';
import { SocketService } from '../../../services/socket/socket.service';

@Component({
  selector: 'app-cart',
  standalone: true,
  imports: [
    CommonModule,
    AppBarComponent,
    LoadingComponent,
    StatusMessageComponent,
    CurrencyPipe,
  ],
  providers: [],
  templateUrl: './cart.component.html',
  styleUrl: './cart.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CartComponent implements OnInit, OnDestroy {
  private menuUrl: string = '';
  uuid: string = '';
  table: Table;
  pageStatusEnum = PageStatusEnum;
  pageStatus: PageStatusEnum | 'complete' = PageStatusEnum.LOADING;

  cart: any[] = [];
  cartData: any = null;
  cartTotal: number = 0;

  phoneData?: Phone;
  phoneDataIsValid: boolean = false;
  isUserLoggedIn: boolean = false;
  loadingMessage: string = 'Carregando...';
  submittingCart: boolean = false;

  backButtonProps: ButtonIconProps = {
    icon: 'arrow-left',
    color: 'neutral',
    variant: 'regular',
    size: 'medium',
  };

  completeButtonProps: Partial<ButtonProps> & { onClick?: () => void } = {
    label: 'Ok, combinado',
    onClick: () => {
      this.eventService.track(EventTrackingEnum.CHECKOUT_CART_CLICKED_ORDER_SENT);
      this.navigateToHome();
    },
  };

  errorButtonProps: Partial<ButtonProps> & { onClick?: () => void } = {
    label: 'Voltar ao início',
    onClick: () => {
      this.navigateToHome();
    },
  };

  authModal: NgxSmartModalComponent;
  user: User | null = null;
  screenViewTimeout: any;
  private authModalSubscription: Subscription | null = null;

  get displayCart(): any[] {
    return this.cart.filter(item => item.name !== 'Taxa de Serviço');
  }

  get serviceFeeValue(): number {
    const serviceFeeItem = this.cart.find(item => item.name === 'Taxa de Serviço');
    return serviceFeeItem ? (serviceFeeItem.total || serviceFeeItem.price) * serviceFeeItem.qty : 0;
  }

  get cartSubtotal(): number {
    return this.displayCart.reduce((acc, item) => acc + (item.total || item.price) * item.qty, 0);
  }

  constructor(
    private cartService: CartService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private vcr: ViewContainerRef,
    private toastr: ToastrService,
    private userService: UserService,
    private tableService: TableService,
    private eventService: EventService,
    private socketService: SocketService,
  ) {
    this.uuid = String(this.activatedRoute.snapshot.paramMap.get('uuid'));
  }

  ngOnInit(): void {
    this.pageStatus = PageStatusEnum.LOADING;
    this.authModal = this.userService.createAuthModal(this.vcr);
    this.isUserLoggedIn = this.userService.checkUser() === 'AUTHORIZED';

    // Order placed elsewhere (e.g. menu landscape sidebar): show success screen directly.
    if (history.state?.orderComplete) {
      this.pageStatus = 'complete';
      return;
    }

    if (this.isUserLoggedIn) {
      this.user = this.userService.getUser();
    }
    this.tableService.$openedTable.subscribe((table: Table | null) => {
      if (table) {
        this.table = table;
      }
    });
    this.cartService.cart$.subscribe((cart: any[]) => {
      this.cart = cart;

      if (this.cart.length > 0) {
        this.pageStatus = PageStatusEnum.SUCCESS;
        this.cartTotal = this.cartService.getCartTotal();

        this.screenViewTimeout = setTimeout(() => {
          this.eventService.track(EventTrackingEnum.CHECKOUT_CART_VIEWED_PAGE);
        }, SCREEN_VIEW_TIMEOUT_IN_MS);
      } else {
        this.pageStatus = PageStatusEnum.ERROR;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.screenViewTimeout) {
      clearTimeout(this.screenViewTimeout);
    }
    if (this.authModalSubscription) {
      this.authModalSubscription.unsubscribe();
      this.authModalSubscription = null;
    }
  }

  private navigateToHome(): void {
    this.router.navigate(['../'], { relativeTo: this.activatedRoute });
  }

  handleBackButtonClick(): void {
    this.router.navigate(['../cardapio'], { relativeTo: this.activatedRoute });
  }

  handleClearCart(): void {
    this.eventService.track(EventTrackingEnum.CHECKOUT_CART_CLICKED_CLEAR);
    this.cartService.clearCart();
    this.router.navigate(['../cardapio'], { relativeTo: this.activatedRoute });
    this.toastr.success(
      'Carrinho esvaziado com sucesso!',
      '',
      {
        toastClass: 'custom-toast-success',
        timeOut: 2000,
        closeButton: false,
      });
  }

  handleContinue(): void {
    if (!this.isUserLoggedIn) {
      if (this.authModalSubscription) {
        this.authModalSubscription.unsubscribe();
        this.authModalSubscription = null;
      }

      this.authModal.setData({
        origin: 'MENU',
      }, true).open();

      this.authModalSubscription = this.authModal.onClose.subscribe((res: any) => {
        if (!res || !res.validated) {
          this.submittingCart = false;
          return;
        }

        this.user = new User(res.id, res.accessToken, res.expiresIn, res.name, res.clientId, res.tableCartId);
        this.submittingCart = true;

        setTimeout(() => {
          if (res.validated) {
            if (!res.name) {
              if (this.authModalSubscription) {
                this.authModalSubscription.unsubscribe();
                this.authModalSubscription = null;
              }
              this.authModal.setData({
                user: this.user,
                step: 'name',
              }, true).open();

              this.authModalSubscription = this.authModal.onClose.subscribe((nameRes: any) => {
                if (nameRes && nameRes.validated && nameRes.name) {
                  this.user = new User(nameRes.id, nameRes.accessToken, nameRes.expiresIn, nameRes.name, nameRes.clientId, nameRes.tableCartId);
                  this.submitCart();
                } else {
                  this.submittingCart = false;
                }
              });
              return;
            }
            this.eventService.track(EventTrackingEnum.CHECKOUT_CART_CLICKED_CREATE_ORDER);
            this.submitCart();
            return;
          }

          this.submittingCart = false;
        }, 1000);
      });
    } else {
      this.eventService.track(EventTrackingEnum.CHECKOUT_CART_CLICKED_CREATE_ORDER);
      this.submitCart();
      this.submittingCart = false;
      return;
    }
  }

  handleAddMoreProducts(): void {
    this.eventService.track(EventTrackingEnum.CHECKOUT_CART_CLICKED_ADD_PRODUCT);
    this.router.navigate(['../cardapio'], { relativeTo: this.activatedRoute });
  }

  // AUTHENTICATION ACTIONS

  private submitCart(): void {
    if (!this.user) {
      console.error('[CART] User not found');
      this.toastr.error('Erro ao identificar usuário', 'Erro');
      return;
    }

    this.loadingMessage = 'Enviando pedido...';
    this.pageStatus = PageStatusEnum.LOADING;
    this.cartService.submitOrder({
      tableId: this.tableService.getTable().tableId,
      clientId: this.user.clientId,
      userId: this.user.id,
      idi: this.cart[0].idi,
    }).subscribe({
      next: () => {
        this.pageStatus = 'complete';
        this.socketService.emit(
          'mesa:update',
          JSON.stringify({
            uuid: crypto.randomUUID(),
          }),
        );
      },
      error: (error) => {
        console.error('[CART] Error submitting cart:', error);
        this.pageStatus = PageStatusEnum.ERROR;
      }
    });
  }

  handlePhoneData(data: Phone): void {
    this.phoneData = data;

    if (this.phoneData.phone?.replace(/\D/g, '').length == this.phoneData.minPhoneSize) {
      this.phoneDataIsValid = true;
    } else {
      this.phoneDataIsValid = false;
    }
  }

  // ITEM ACTIONS

  handleQuantityChange(item: any, event: any): void {
    const newQty = Number(event.detail.value);

    if (newQty > 99) {
      this.cartService.editItemQuantity(item.cartItemId, 99);
      return;
    }

    this.cartService.editItemQuantity(item.cartItemId, newQty);

    if (newQty === 0) {
      this.toastr.success(
        'Item removido do carrinho',
        '',
        {
          toastClass: 'custom-toast-success',
          timeOut: 2000,
          closeButton: false,
        }
      );
      if (this.cart.length === 0) {
        this.handleBackButtonClick();
        return;
      }
    }
  }

  handleEditItem(item: any): void {
    const productModal = this.cartService.createProductModal(
      { ...item, choices: item.availableChoices }, // Pass available choices in product
      this.vcr,
      item.qty,
      item.cartItemId,
      item.selectedChoices, // Pass selected choices separately
      item.additional_info // Pass additional info for editing
    );

    productModal.open();
    productModal.onClose.subscribe((res: any) => {

    });
  }
}
